import React, { useState, useEffect, useLayoutEffect } from 'react';
import { Link } from 'react-router-dom';
import { setSEO, setPageJsonLd, clearPageJsonLd } from './seo';
import {
  ArrowRight, Check, Clock, Shield, Eye, MapPin, Cpu, FileCheck, Paperclip, ChevronDown, Download,
} from 'lucide-react';
import Simulator from './Simulator';
import { Nav, Footer } from './SharedUI';
import { STRIPE_LINKS } from './data/stripeLinks';
import { trackLead, trackCheckout as trackCheckoutCentral } from './data/tracking';
import { T, FONT_BODY, FONT_MONO, FONT_SERIF, eyebrow, pad2 } from './styleEditorial';
import {
  MagneticButton, Guilloche, Counter, RevealOnScroll, DCFSchema, ValuationRangeChart,
} from './components/premiumKit';
import EditorialImageBand from './components/EditorialImageBand';

// Wrapper local pour préserver la signature historique (serviceName, priceStr)
function trackCheckout(serviceName, priceStr) {
  return trackCheckoutCentral({ serviceName, priceStr });
}

// ─── Données de la page ──────────────────────────────────────────────────

const REPORT_SAMPLES = [
  {
    key: 'express',
    name: 'Pré-diagnostic Express',
    pages: '5 pages · livré sous 24 h',
    price: '49 €',
    contents: 'Score financier 0-100, quatre ratios bancaires, comparaison avec la médiane de votre secteur, trois recommandations chiffrées.',
    pdf: '/exemples/express-exemple.pdf',
  },
  {
    key: 'previsionnel',
    name: 'Prévisionnel financier',
    pages: '18 pages · livré sous 48 h',
    price: '500 €',
    contents: 'Projection sur 3 ans, plan de trésorerie mensuel, seuil de rentabilité — au format attendu par les banques et BPI France.',
    pdf: '/exemples/previsionnel-exemple.pdf',
  },
  {
    key: 'valorisation',
    name: 'Valorisation d’entreprise',
    pages: '20 pages · livré sous 48 h',
    price: '900 €',
    contents: 'Trois méthodes pondérées, comparables sectoriels, fourchette de valeur défendable en négociation.',
    pdf: '/exemples/valorisation-exemple.pdf',
  },
];

const OFFER_TIERS = [
  {
    num: '01',
    name: 'Découvrir',
    range: '0 — 49 €',
    promise: 'Savoir où vous en êtes, aujourd’hui.',
    detail: 'Score de santé financière gratuit en 2 minutes. Pour aller plus loin, le mini-rapport Express : 5 pages, livré sous 24 h, déduit de toute commande ultérieure.',
    links: [
      { label: 'Diagnostic gratuit', to: '/diagnostic-gratuit', kind: 'route' },
      { label: 'Express — 49 €', href: STRIPE_LINKS['express'], kind: 'stripe', serviceName: 'Pré-diagnostic Express — 49€', price: '49€' },
    ],
  },
  {
    num: '02',
    name: 'Décider',
    range: '300 — 700 €',
    promise: 'Convaincre votre banque, piloter vos choix.',
    detail: 'Analyse financière, audit de trésorerie, prévisionnel ou business plan. Le dossier chiffré qui appuie un crédit, une subvention ou un cap stratégique.',
    links: [
      { label: 'Prévisionnel — 500 €', to: '/previsionnel', kind: 'route' },
      { label: 'Audit trésorerie — 350 €', to: '/audit-tresorerie', kind: 'route' },
      { label: 'Business plan — 700 €', to: '/business-plan', kind: 'route' },
    ],
  },
  {
    num: '03',
    name: 'Transmettre',
    range: '900 — 1 500 €',
    promise: 'Vendre, s’associer, lever — au juste prix.',
    detail: 'Valorisation complète ou dossier de cession. La valeur de votre entreprise, établie méthode par méthode, défendable point par point face à un repreneur.',
    links: [
      { label: 'Valorisation — 900 €', to: '/valorisation', kind: 'route' },
      { label: 'Dossier cession — 1 200 €', to: '/cession', kind: 'route' },
    ],
  },
];

const STEPS = [
  {
    num: '01',
    title: 'Vous répondez à quelques questions',
    desc: 'Deux minutes en ligne, gratuit. Vous recevez immédiatement votre score et le service adapté à votre situation — parfois, aucun.',
  },
  {
    num: '02',
    title: 'Vous transmettez vos documents',
    desc: 'Bilans, liasse fiscale, prévisions. Tout se fait en ligne, chiffré, strictement confidentiel. Accord de confidentialité signé sur simple demande.',
  },
  {
    num: '03',
    title: 'Vous recevez votre rapport sous 48 h',
    desc: 'Un PDF structuré, sources citées, prêt à poser devant votre banquier, votre associé ou votre repreneur. Un échange de 30 minutes est inclus.',
  },
];

const GUARANTEES = [
  { icon: Clock, title: 'Livraison 48 h', desc: 'après réception complète de vos documents' },
  { icon: Shield, title: 'Confidentiel', desc: 'documents chiffrés, jamais partagés, NDA sur demande' },
  { icon: Check, title: 'Satisfait ou remboursé', desc: 'refait gratuitement, ou remboursé sous 7 jours' },
  { icon: MapPin, title: 'France entière', desc: '100 % en ligne, métropole et DOM-TOM' },
];

const FOUNDER = {
  name: 'Jérôme Rognon',
  initials: 'JR',
  role: 'Fondateur de FinAnalyse',
  photo: '/founder.jpg',
  expertise: [
    { icon: Cpu, label: 'Outil d’analyse développé en interne' },
    { icon: FileCheck, label: 'Rapports structurés selon les standards bancaires' },
    { icon: Eye, label: 'Relecture humaine systématique avant envoi' },
    { icon: MapPin, label: 'Basé en France — contact direct par email' },
  ],
  story: [
    'Les dirigeants de PME ont besoin d’analyses financières — pour leur banque, leurs associés, leur stratégie. Mais les cabinets facturent 5 000 à 15 000 € le dossier. Un budget hors de portée pour la plupart.',
    'J’ai donc construit FinAnalyse : un outil interne qui applique la méthodologie des cabinets d’affaires — DCF, multiples sectoriels, ratios bancaires — aux référentiels Banque de France et INSEE, pour produire des rapports structurés en 48 heures.',
    'Chaque rapport est ensuite relu et validé manuellement avant envoi. Pas de magie : de la technologie bien orchestrée, et beaucoup d’attention au détail.',
  ],
  quote: 'Mon objectif n’est pas de remplacer votre expert-comptable. C’est de vous donner les outils chiffrés dont vous avez besoin pour piloter, négocier et décider.',
};

const TESTIMONIAL = {
  name: 'Maxime',
  role: 'Fondateur, Hyrox Neuchâtel · Suisse',
  text: 'Super retour, merci pour ta rapidité et ton professionnalisme ! L’étude livrée a parfaitement répondu à nos attentes.',
  date: 'Mai 2026',
};

const FAQS = [
  { q: 'Vos rapports sont-ils générés par intelligence artificielle ?', a: 'Oui, et c’est assumé : nous avons développé un outil propriétaire qui combine IA, méthodologie d’analyse financière française (DCF, multiples sectoriels, ratios bancaires) et benchmarks Banque de France/INSEE. Chaque rapport est ensuite révisé manuellement avant envoi pour vérifier la cohérence et la plausibilité des chiffres. Cette automatisation intelligente est ce qui nous permet de facturer 10x moins cher qu’un cabinet traditionnel.' },
  { q: 'Vous êtes expert-comptable ?', a: 'Non, et nous ne prétendons pas l’être. FinAnalyse est un service d’analyse complémentaire — nos rapports ne remplacent pas un expert-comptable certifié pour la tenue de comptabilité ou le commissariat. En revanche, ils sont parfaitement adaptés pour structurer un dossier bancaire, préparer une cession, valider une stratégie ou piloter votre PME au mois le mois.' },
  { q: 'Le prévisionnel est-il accepté par les banques ?', a: 'Oui. Notre prévisionnel est structuré selon les standards bancaires : projection CA sur 3 ans, plan de trésorerie mensuel, bilan prévisionnel, seuil de rentabilité. Nos clients l’ont utilisé avec succès auprès du Crédit Agricole, BNP, CIC, Banque Populaire et BPI France.' },
  { q: 'Quels documents faut-il fournir ?', a: 'Pour la valorisation/analyse : bilans et comptes de résultats 3 ans + liasse fiscale. Pour le prévisionnel : hypothèses de croissance et projets d’investissement. Pour l’audit trésorerie : grand livre, balance âgée clients/fournisseurs. Nous guidons chaque client pour la collecte.' },
  { q: 'Mes données financières sont-elles confidentielles ?', a: 'Absolument. Vos documents sont traités de façon strictement confidentielle, chiffrés, jamais partagés avec des tiers. Nous pouvons signer un accord de confidentialité (NDA) sur demande.' },
  { q: 'Que se passe-t-il si je ne suis pas satisfait ?', a: 'Vous disposez de 7 jours après la livraison pour demander un remboursement intégral, sans condition ni justification. Si vous préférez, nous pouvons aussi refaire gratuitement le rapport sur la base de vos retours — c\'est votre choix, jamais une obligation avant remboursement. Le remboursement est effectué sous 7 jours sur votre moyen de paiement.' },
];

// ─── Petits composants éditoriaux locaux ─────────────────────────────────

function SectionHead({ tag, title, sub, light = false }) {
  return (
    <RevealOnScroll className="ed-section-head" style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '40px', alignItems: 'baseline', marginBottom: '48px' }}>
      <div style={{ ...eyebrow, fontSize: '13px', color: light ? T.goldSoft : T.goldDeep }}>{tag}</div>
      <div>
        <h2 style={{ fontFamily: FONT_SERIF, fontSize: 'clamp(30px, 4vw, 46px)', fontWeight: 400, lineHeight: 1.1, letterSpacing: '-0.01em', color: light ? T.paper : T.ink, margin: 0 }}>
          {title}
        </h2>
        {sub && (
          <p style={{ fontFamily: FONT_BODY, fontSize: '16px', color: light ? 'rgba(250,248,243,0.75)' : T.inkSoft, margin: '12px 0 0 0', maxWidth: '640px', lineHeight: 1.6 }}>
            {sub}
          </p>
        )}
      </div>
    </RevealOnScroll>
  );
}

function FaqItem({ q, a, idx, open, onToggle }) {
  const panelId = `faq-panel-${idx}`;
  return (
    <div style={{ borderBottom: `1px solid ${T.ruleLight}` }}>
      <button
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={panelId}
        className="ed-faq-btn"
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '24px',
          width: '100%', padding: '22px 4px', minHeight: '44px',
          background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
          fontFamily: FONT_BODY, fontSize: '17px', fontWeight: 500, color: T.ink,
        }}
      >
        {q}
        <ChevronDown size={18} style={{ color: T.goldDeep, flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s cubic-bezier(0.16,1,0.3,1)' }} />
      </button>
      <div id={panelId} style={{ display: 'grid', gridTemplateRows: open ? '1fr' : '0fr', transition: 'grid-template-rows 0.35s cubic-bezier(0.16,1,0.3,1)' }}>
        <div style={{ overflow: 'hidden' }}>
          <p style={{ fontFamily: FONT_BODY, fontSize: '15px', lineHeight: 1.65, color: T.inkSoft, margin: 0, padding: '0 40px 24px 4px', maxWidth: '760px' }}>
            {a}
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────

export default function FinAnalyse() {
  const [openFaq, setOpenFaq] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', company: '', phone: '', service: '', message: '' });
  const [formSent, setFormSent] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [files, setFiles] = useState([]);

  // ── Entrée cinématique du hero ──────────────────────────────────────────
  // `entered` démarre à TRUE : prerender (Puppeteer) + 1er rendu d'hydratation
  // affichent l'état final visible → SEO sûr, zéro mismatch. Pour un VRAI visiteur
  // (pas bot, pas reduced-motion), useLayoutEffect rebascule à l'état caché AVANT
  // le paint, puis relâche l'animation → aucun flash.
  const [entered, setEntered] = useState(true);
  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.navigator.webdriver) return;
    if (typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    setEntered(false);
    const raf = requestAnimationFrame(() => requestAnimationFrame(() => setEntered(true)));
    return () => cancelAnimationFrame(raf);
  }, []);

  // Respect de prefers-reduced-motion pour les guilloches en rotation permanente
  // (animation inline non surchargeable par CSS). Démarre false (= comme le pré-rendu),
  // passe true au mount si l'utilisateur demande moins d'animations → animation coupée.
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    if (typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setReducedMotion(true);
    }
  }, []);
  const spin = (css) => (reducedMotion ? 'none' : css);

  const rise = (delay, y = 26) => ({
    opacity: entered ? 1 : 0,
    transform: entered ? 'translateY(0)' : `translateY(${y}px)`,
    transition: `opacity 0.9s cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 1s cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
  });

  useEffect(() => {
    setSEO({
      title: 'FinAnalyse — Cabinet d’analyse financière PME : valorisation, prévisionnel, audit en 48 h',
      description: 'Valorisation d’entreprise, prévisionnel bancaire, audit de trésorerie, business plan. Méthodes des cabinets M&A, benchmarks Banque de France, relecture humaine. Rapports livrés en 48 h, dès 49 €.',
      path: '/',
    });

    // Balisage propre à l'accueil. GÉNÉRÉ depuis FAQS et STEPS, les données qui
    // servent aussi au rendu : le balisage ne peut donc pas diverger du contenu
    // visible. C'est exactement ce qui n'allait pas avant l'audit du 2026-08-15 —
    // index.html déclarait une FAQPage de 11 questions et un HowTo de 5 étapes,
    // recopiés sur les 82 pages, alors que l'accueil en affiche 6 et 3.
    clearPageJsonLd();
    setPageJsonLd('jsonld-home-service', {
      '@type': 'ProfessionalService',
      name: 'FinAnalyse',
      url: 'https://www.finanalyse.fr/',
      description:
        'Cabinet d’analyse financière pour PME : valorisation d’entreprise, prévisionnel bancaire, audit de trésorerie et business plan, selon les méthodes des cabinets M&A (DCF, multiples sectoriels) et les benchmarks Banque de France, avec relecture humaine systématique.',
      email: 'contact@finanalyse.fr',
      areaServed: { '@type': 'Country', name: 'France' },
      availableLanguage: 'Français',
      priceRange: '€€',
      parentOrganization: { '@id': 'https://www.finanalyse.fr/#organization' },
    });
    setPageJsonLd('jsonld-home-faq', {
      '@type': 'FAQPage',
      mainEntity: FAQS.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
    setPageJsonLd('jsonld-home-howto', {
      '@type': 'HowTo',
      name: 'Obtenir son analyse financière avec FinAnalyse',
      description: 'Le déroulé en trois étapes, du diagnostic gratuit au rapport livré sous 48 h.',
      totalTime: 'PT48H',
      step: STEPS.map((s, i) => ({
        '@type': 'HowToStep',
        position: i + 1,
        name: s.title,
        text: s.desc,
      })),
    });
    // En navigation SPA, quitter la page doit emporter son balisage : sans ça,
    // la FAQPage de l'accueil resterait posée sur la page suivante.
    return () => clearPageJsonLd();
  }, []);

  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || '').replace(/^data:[^;]+;base64,/, ''));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formSubmitting) return;

    const MAX_FILE_SIZE = 3 * 1024 * 1024;
    const MAX_TOTAL_SIZE = 5 * 1024 * 1024;
    if (files.length > 5) {
      setFormError('Maximum 5 fichiers. Envoyez le reste plus tard à contact@finanalyse.fr.');
      return;
    }
    const tooBig = files.find(f => f.size > MAX_FILE_SIZE);
    if (tooBig) {
      setFormError(`« ${tooBig.name} » fait ${(tooBig.size / 1024 / 1024).toFixed(1)} Mo. Max 3 Mo par fichier.`);
      return;
    }
    const totalSize = files.reduce((s, f) => s + f.size, 0);
    if (totalSize > MAX_TOTAL_SIZE) {
      setFormError(`Total ${(totalSize / 1024 / 1024).toFixed(1)} Mo. Max 5 Mo au total.`);
      return;
    }

    setFormSubmitting(true);
    setFormError(null);

    try {
      const filesPayload = await Promise.all(files.map(async (f) => ({
        name: f.name, size: f.size, type: f.type || 'application/octet-stream',
        content: await fileToBase64(f),
      })));

      const r = await fetch('/api/contact-upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company,
          phone: formData.phone,
          service: formData.service || 'Demande générale',
          message: formData.message,
          files: filesPayload,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok && data.ok) {
        await trackLead({
          source: 'homepage-contact',
          value: 5,
          email: formData.email,
          phone: formData.phone,
          name: formData.name,
        });
        window.location.href = `/merci?source=contact-form&value=5&name=${encodeURIComponent(formData.name)}&email=${encodeURIComponent(formData.email)}`;
        return;
      } else {
        setFormError(data.error || 'Échec de l’envoi. Réessayez ou écrivez à contact@finanalyse.fr');
      }
    } catch (err) {
      console.error('[homepage contact] submit error:', err);
      setFormError('Erreur réseau. Réessayez ou écrivez à contact@finanalyse.fr');
    } finally {
      setFormSubmitting(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '12px 14px', border: `1px solid ${T.ruleLight}`,
    background: T.paperWhite, fontSize: '15px', fontFamily: FONT_BODY, color: T.ink,
    boxSizing: 'border-box', borderRadius: 0, outline: 'none',
  };
  const labelStyle = { display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px', color: T.ink, fontFamily: FONT_BODY };

  return (
    <div style={{ fontFamily: FONT_BODY, color: T.ink, background: T.paper, WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale', position: 'relative' }}>
      <div aria-hidden="true" style={{
        position: 'fixed', inset: 0,
        backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='180' height='180'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.45 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>")`,
        // Sans mix-blend-mode : évite un re-blend/repaint du viewport entier à chaque frame de scroll.
        backgroundSize: '180px 180px', pointerEvents: 'none', zIndex: 1, opacity: 0.12,
      }} />

      <div style={{ position: 'relative', zIndex: 2 }}>

      <Nav />

      {/* <main> : la page n'avait aucun repère de contenu principal (constat
          Lighthouse « Document does not have a main landmark », audit 2026-08-15).
          Il pénalisait aussi le score « Agentic Browsing » — les agents IA ne
          savaient pas où commence le contenu. */}
      <main>

      {/* ══ 1 · HERO CINÉMATIQUE ══ */}
      <header className="ed-hero-header" style={{ padding: '76px 32px 0', background: T.paper, position: 'relative', overflow: 'hidden', borderBottom: `1px solid ${T.ink}` }}>
        <div aria-hidden="true" className="ed-glow" style={{
          position: 'absolute', top: '-10%', right: '4%', width: '620px', height: '620px',
          background: `radial-gradient(circle, ${T.gold}22 0%, ${T.gold}0D 40%, transparent 70%)`,
          pointerEvents: 'none', zIndex: 0,
        }} />
        <Guilloche size={640} color={T.gold} opacity={0.05} rings={18}
          style={{ position: 'absolute', top: '-180px', left: '-160px', zIndex: 0, animation: spin('edSpin 90s linear infinite') }} />
        <div aria-hidden="true" className="ed-rail" style={{ position: 'absolute', left: '8%', top: 0, bottom: 0, width: '1px', background: T.ruleLight, opacity: 0.5, zIndex: 0 }} />

        <div style={{ maxWidth: '1240px', margin: '0 auto', position: 'relative', zIndex: 2, paddingBottom: '80px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '40px', flexWrap: 'wrap', gap: '16px', ...rise(0) }}>
            <div style={{ ...eyebrow, fontSize: '13px', display: 'flex', alignItems: 'center', gap: '14px' }}>
              <span style={{ display: 'inline-block', width: entered ? '28px' : '0px', height: '1px', background: T.ink, transition: 'width 0.8s cubic-bezier(0.16,1,0.3,1) 0.2s' }} />
              Cabinet d’analyse financière — 100 % en ligne
            </div>
            <div style={{ fontFamily: FONT_MONO, fontSize: '13px', color: T.goldDeep, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
              P. 01 — Accueil
            </div>
          </div>

          <h1 className="ed-hero-h1" style={{
            fontFamily: FONT_SERIF, fontSize: 'clamp(42px, 6.4vw, 88px)', fontWeight: 400,
            lineHeight: 1.03, letterSpacing: '-0.015em', color: T.ink, margin: '0 0 44px 0', maxWidth: '1040px',
          }}>
            {[
              <>Sachez ce que vaut</>,
              <>votre PME <em style={{ fontStyle: 'italic', fontWeight: 600, color: T.gold }} className="ed-shimmer">avant votre</em></>,
              <><em style={{ fontStyle: 'italic', fontWeight: 600, color: T.gold }} className="ed-shimmer">prochaine négociation</em>.</>,
            ].map((line, i) => (
              <span key={i} style={{ display: 'block', overflow: 'hidden' }}>
                <span style={{
                  display: 'block',
                  transform: entered ? 'translateY(0)' : 'translateY(105%)',
                  transition: `transform 1s cubic-bezier(0.16,1,0.3,1) ${120 + i * 130}ms`,
                }}>
                  {line}
                </span>
              </span>
            ))}
          </h1>

          <div className="ed-hero-grid" style={{ display: 'grid', gridTemplateColumns: '1.15fr 0.85fr', gap: '72px', alignItems: 'start' }}>
            <div>
              <p style={{ fontFamily: FONT_BODY, fontSize: '19px', lineHeight: 1.65, color: T.inkSoft, margin: '0 0 36px 0', maxWidth: '540px', ...rise(560) }}>
                Banque, associé, repreneur : ne discutez plus à l’aveugle.
                Un rapport écrit selon <strong style={{ color: T.ink, fontWeight: 600 }}>les méthodes des cabinets d’affaires</strong>,
                relu par un analyste, livré sous 48 h. <strong style={{ color: T.ink, fontWeight: 600 }}>À partir de 49 €</strong>, sans abonnement.
              </p>

              <div style={{ display: 'flex', alignItems: 'center', gap: '28px', flexWrap: 'wrap', marginBottom: '52px', ...rise(660) }}>
                <MagneticButton href="#simulateur" strength={0.3} maxOffset={12} className="ed-cta-primary" style={{ padding: '18px 36px', background: T.ink, color: T.paper, border: `1px solid ${T.ink}`, fontFamily: FONT_BODY, fontSize: '14px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', textDecoration: 'none', alignItems: 'center', gap: '12px', position: 'relative', overflow: 'hidden' }}>
                  <span style={{ position: 'relative', zIndex: 1, display: 'inline-flex', alignItems: 'center', gap: '12px' }}>Estimer ma PME — gratuit <ArrowRight size={16} aria-hidden="true" /></span>
                </MagneticButton>
                <a href="#honoraires" className="ed-link-underline" style={{ fontFamily: FONT_BODY, fontSize: '15px', fontWeight: 500, color: T.ink, textDecoration: 'none', minHeight: '44px', display: 'inline-flex', alignItems: 'center' }}>
                  Voir les honoraires
                </a>
              </div>

              {/* Réduction du risque, juste au point de clic */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '40px', fontFamily: FONT_BODY, fontSize: '13.5px', color: T.inkSoft, ...rise(720) }}>
                <Check size={16} style={{ color: T.goldDeep, flexShrink: 0 }} aria-hidden="true" />
                <span><strong style={{ color: T.ink, fontWeight: 600 }}>Satisfait ou remboursé sous 7 jours</strong>, sans justification. Sans inscription, sans engagement.</span>
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '44px', paddingTop: '28px', borderTop: `1px solid ${T.ruleLight}`, ...rise(780) }}>
                {[
                  { to: 48, suffix: ' h', l: 'délai de livraison' },
                  { to: 3, suffix: '', l: 'méthodes de valorisation' },
                  { to: 7, suffix: ' j', l: 'satisfait ou remboursé' },
                ].map((f) => (
                  <div key={f.l}>
                    <Counter to={f.to} suffix={f.suffix} style={{ fontSize: '26px', fontWeight: 500, color: T.ink, letterSpacing: '-0.02em', lineHeight: 1, display: 'inline-block' }} />
                    <div style={{ fontFamily: FONT_BODY, fontSize: '13px', color: T.muted, marginTop: '7px', letterSpacing: '0.04em' }}>{f.l}</div>
                  </div>
                ))}
              </div>
            </div>

            <aside className="ed-report-card ed-float" style={{
              background: T.paperWhite, padding: '36px 32px 30px', border: `1px solid ${T.ink}`, position: 'relative', overflow: 'hidden',
              opacity: entered ? 1 : 0, transform: entered ? 'translateX(0)' : 'translateX(40px)',
              transition: 'opacity 1.1s cubic-bezier(0.16,1,0.3,1) 400ms, transform 1.1s cubic-bezier(0.16,1,0.3,1) 400ms',
              boxShadow: '0 30px 80px -40px rgba(10,25,41,0.35)',
            }}>
              <Guilloche size={280} color={T.gold} opacity={0.08} rings={12} style={{ position: 'absolute', top: '-80px', left: '-80px', zIndex: 0, animation: spin('edSpin 70s linear infinite reverse') }} />
              <div aria-hidden="true" style={{ position: 'absolute', top: '-1px', right: '-1px', width: '52px', height: '52px', background: T.gold, clipPath: 'polygon(100% 0, 100% 100%, 0 0)', zIndex: 1 }} />

              <div style={{ ...eyebrow, fontSize: '13px', color: T.goldDeep, marginBottom: '20px', position: 'relative', zIndex: 1 }}>
                Extrait de rapport · N° 247
              </div>

              <div style={{ paddingBottom: '20px', borderBottom: `1px solid ${T.ruleLight}`, marginBottom: '20px', position: 'relative', zIndex: 1 }}>
                <div style={{ ...eyebrow, fontSize: '13px', color: T.muted, marginBottom: '4px' }}>Valorisation d’entreprise</div>
                <div style={{ fontFamily: FONT_SERIF, fontSize: '24px', fontWeight: 500, color: T.ink, letterSpacing: '-0.01em', lineHeight: 1.2 }}>
                  Atelier Dubois SAS
                </div>
              </div>

              <div style={{ marginBottom: '24px', position: 'relative', zIndex: 1 }}>
                <div style={{ ...eyebrow, fontSize: '13px', color: T.muted, marginBottom: '6px' }}>Valeur centrale</div>
                <div style={{ fontFamily: FONT_MONO, fontSize: '46px', fontWeight: 500, color: T.ink, lineHeight: 1, letterSpacing: '-0.02em' }}>
                  <Counter to={495} suffix=" K€" duration={1900} style={{ fontSize: '46px' }} />
                </div>
                <div style={{ fontFamily: FONT_BODY, fontSize: '13px', color: T.muted, marginTop: '7px', fontStyle: 'italic' }}>
                  Fourchette : 425 — 580 K€
                </div>
              </div>

              <div style={{ marginBottom: '20px', position: 'relative', zIndex: 1 }}>
                <div style={{ ...eyebrow, fontSize: '13px', color: T.muted, marginBottom: '12px', borderBottom: `1px solid ${T.ink}`, paddingBottom: '6px' }}>
                  Méthodes pondérées
                </div>
                {[
                  ['Multiples EBITDA', '4,5×', '60 %'],
                  ['DCF actualisé', '555 K€', '25 %'],
                  ['Actif net réévalué', '433 K€', '15 %'],
                ].map(([m, v, p], i) => (
                  <div key={m} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '10px 0', fontSize: '13.5px',
                    borderBottom: i < 2 ? `1px solid ${T.ruleLight}` : 'none', fontFamily: FONT_BODY,
                    opacity: entered ? 1 : 0, transform: entered ? 'none' : 'translateX(12px)',
                    transition: `opacity 0.6s ease ${900 + i * 130}ms, transform 0.6s ease ${900 + i * 130}ms`,
                  }}>
                    <span style={{ color: T.ink, fontWeight: 500 }}>{m}</span>
                    <span style={{ fontFamily: FONT_MONO, color: T.ink, fontWeight: 500 }}>{v}<span style={{ color: T.goldDeep, fontWeight: 400, fontSize: '13px', marginLeft: '8px' }}>{p}</span></span>
                  </div>
                ))}
              </div>

              <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{ ...eyebrow, fontSize: '13px', color: T.muted, marginBottom: '8px' }}>Évolution 2018 — 2025</div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '5px', height: '48px', marginBottom: '6px' }}>
                  {[35, 52, 70, 65, 88, 75, 92, 100].map((h, i) => (
                    <div key={i} style={{
                      flex: 1, background: i === 7 ? T.gold : T.ink, opacity: i === 7 ? 1 : 0.85,
                      height: entered ? `${h}%` : '0%',
                      transition: `height 0.8s cubic-bezier(0.16,1,0.3,1) ${1000 + i * 70}ms`,
                    }} />
                  ))}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: FONT_MONO, fontSize: '11px', color: T.muted }}>
                  <span aria-hidden="true">'18</span><span aria-hidden="true">'25</span>
                </div>
              </div>
            </aside>
          </div>

          <div aria-hidden="true" style={{ display: 'flex', justifyContent: 'center', marginTop: '64px', ...rise(1000) }}>
            <span className="ed-scroll-cue" style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: '8px', fontFamily: FONT_MONO, fontSize: '10px', letterSpacing: '0.25em', color: T.muted, textTransform: 'uppercase' }}>
              Défiler
              <ChevronDown size={16} style={{ color: T.goldDeep }} />
            </span>
          </div>
        </div>
      </header>

      {/* ══ BANDEAU DE CONFIANCE — banques + sécurité ══ */}
      <section aria-label="Confiance et sécurité" style={{ background: T.ink, color: T.paper, padding: '28px 32px', borderBottom: `1px solid ${T.ink}` }}>
        <div className="ed-trust-grid" style={{ maxWidth: '1240px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr auto', gap: '32px 48px', alignItems: 'center' }}>
          <div>
            <div style={{ ...eyebrow, fontSize: '12px', color: T.goldSoft, marginBottom: '12px' }}>Dossiers déjà présentés avec succès à</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 26px', alignItems: 'baseline' }}>
              {['Crédit Agricole', 'BNP Paribas', 'CIC', 'Banque Populaire', 'BPI France'].map((b) => (
                <span key={b} style={{ fontFamily: FONT_SERIF, fontSize: '19px', fontWeight: 500, color: T.paper }}>{b}</span>
              ))}
            </div>
          </div>
          <div className="ed-trust-badges" style={{ display: 'flex', alignItems: 'center', gap: '10px', fontFamily: FONT_BODY, fontSize: '13px', color: 'rgba(250,248,243,0.78)', flexWrap: 'wrap' }}>
            <Shield size={15} style={{ color: T.goldSoft, flexShrink: 0 }} aria-hidden="true" />
            <span>Paiement Stripe sécurisé</span><span style={{ color: T.gold }}>·</span>
            <span>Hébergement France</span><span style={{ color: T.gold }}>·</span>
            <span>Conforme RGPD</span><span style={{ color: T.gold }}>·</span>
            <span>Données chiffrées</span>
          </div>
        </div>
      </section>

      {/* ══ 2 · LE LIVRABLE ══ */}
      <section style={{ padding: '84px 32px', background: T.paperWarm, borderBottom: `1px solid ${T.ink}` }}>
        <div style={{ maxWidth: '1240px', margin: '0 auto' }}>
          <SectionHead
            tag="§ 01 · Le livrable"
            title={<>Un rapport que vous pouvez <em style={{ fontStyle: 'italic', color: T.gold, fontWeight: 600 }}>poser sur la table</em>.</>}
            sub="Pas un chat, pas un tableau de bord jetable : un document écrit, chiffré, sources citées. Feuilletez un exemple avant de commander — établi sur un cas fictif, Atelier Dubois SAS."
          />

          <RevealOnScroll delay={80} style={{ maxWidth: '460px', margin: '0 0 40px', padding: '24px 28px', background: T.paperWhite, border: `1px solid ${T.ruleLight}` }}>
            <ValuationRangeChart />
          </RevealOnScroll>

          <div className="ed-3col" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0', borderTop: `1px solid ${T.ink}` }}>
            {REPORT_SAMPLES.map((r, i) => (
              <RevealOnScroll as="article" key={r.key} delay={i * 110} className="ed-report-sample" style={{ padding: '32px 28px 28px', borderRight: i < 2 ? `1px solid ${T.ruleLight}` : 'none', borderBottom: `1px solid ${T.ruleLight}`, display: 'flex', flexDirection: 'column' }}>
                <div style={{ fontFamily: FONT_MONO, fontSize: '13px', color: T.goldDeep, letterSpacing: '0.05em', marginBottom: '14px' }}>{pad2(i + 1)}</div>
                <h3 style={{ fontFamily: FONT_SERIF, fontSize: '22px', fontWeight: 500, color: T.ink, margin: '0 0 6px 0', letterSpacing: '-0.01em' }}>{r.name}</h3>
                <div style={{ fontFamily: FONT_MONO, fontSize: '13px', color: T.muted, marginBottom: '16px' }}>{r.pages}</div>
                <p style={{ fontFamily: FONT_BODY, fontSize: '14.5px', lineHeight: 1.6, color: T.inkSoft, margin: '0 0 22px 0', flex: 1 }}>
                  {r.contents}
                </p>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                  <a href={r.pdf} target="_blank" rel="noopener noreferrer" className="ed-link-underline" style={{ fontFamily: FONT_BODY, fontSize: '14px', fontWeight: 600, color: T.ink, textDecoration: 'none', minHeight: '44px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                    Voir le PDF exemple <ArrowRight size={14} aria-hidden="true" />
                  </a>
                  <span style={{ fontFamily: FONT_MONO, fontSize: '15px', color: T.goldDeep, fontWeight: 500 }}>{r.price}</span>
                </div>
              </RevealOnScroll>
            ))}
          </div>

          {/* Bloc de téléchargement — voir le vrai livrable avant d'acheter */}
          <RevealOnScroll className="ed-download" style={{ marginTop: '28px', padding: '28px 32px', background: T.ink, color: T.paper, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '28px', flexWrap: 'wrap', position: 'relative', overflow: 'hidden' }}>
            <div aria-hidden="true" style={{ position: 'absolute', top: '-1px', right: '-1px', width: '48px', height: '48px', background: T.gold, clipPath: 'polygon(100% 0, 100% 100%, 0 0)' }} />
            <div style={{ maxWidth: '620px' }}>
              <div style={{ ...eyebrow, fontSize: '12px', color: T.goldSoft, marginBottom: '10px' }}>Voir exactement ce que vous recevez</div>
              <div style={{ fontFamily: FONT_SERIF, fontSize: '23px', fontWeight: 500, color: T.paper, lineHeight: 1.25, marginBottom: '8px' }}>
                Téléchargez un rapport de valorisation complet.
              </div>
              <p style={{ fontFamily: FONT_BODY, fontSize: '14.5px', color: 'rgba(250,248,243,0.72)', margin: 0, lineHeight: 1.55 }}>
                20 pages, cas anonymisé « Atelier Dubois SAS » — page de garde, sommaire, trois méthodes, plan d’action. Le format exact que vous poserez devant votre banque.
              </p>
            </div>
            <a href="/exemples/valorisation-exemple.pdf" target="_blank" rel="noopener noreferrer" download className="ed-cta-gold" style={{ display: 'inline-flex', alignItems: 'center', gap: '12px', padding: '16px 30px', background: T.gold, color: T.ink, border: `1px solid ${T.gold}`, fontFamily: FONT_BODY, fontSize: '13.5px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', textDecoration: 'none', whiteSpace: 'nowrap' }}>
              <Download size={16} aria-hidden="true" /> Télécharger le PDF (20 p.)
            </a>
          </RevealOnScroll>
          <RevealOnScroll as="p" style={{ marginTop: '16px', fontFamily: FONT_BODY, fontSize: '13.5px', color: T.muted, lineHeight: 1.6 }}>
            Vous préférez un autre type de rapport ?{' '}
            <a href="mailto:contact@finanalyse.fr?subject=Exemple%20de%20rapport%20complet" className="ed-link-underline" style={{ color: T.ink, fontWeight: 500, textDecoration: 'none' }}>
              Recevoir un exemple par email
            </a>
          </RevealOnScroll>
        </div>
      </section>

      {/* ══ 2b · COMPARATIF PRIX — l'argument massue ══ */}
      <section style={{ padding: '84px 32px', background: T.paper, borderBottom: `1px solid ${T.ink}` }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <SectionHead
            tag="§ 02 · Le juste prix"
            title={<>La même méthode, <em style={{ fontStyle: 'italic', color: T.gold, fontWeight: 600 }}>pas le même tarif</em>.</>}
            sub="Nos rapports appliquent exactement les méthodes des cabinets d’affaires — mais notre outil interne fait le gros du travail. Vous ne payez pas trois semaines d’honoraires."
          />

          <div style={{ overflowX: 'auto' }}>
            <RevealOnScroll style={{ border: `1px solid ${T.ink}`, minWidth: '600px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr 1fr', borderBottom: `1px solid ${T.ink}` }}>
                <div style={{ padding: '18px 24px' }} />
                <div style={{ padding: '18px 24px', borderLeft: `1px solid ${T.ruleLight}` }}>
                  <div style={{ ...eyebrow, fontSize: '12px', color: T.muted }}>Cabinet M&amp;A traditionnel</div>
                </div>
                <div style={{ padding: '18px 24px', borderLeft: `1px solid ${T.gold}`, background: T.paperWhite, position: 'relative' }}>
                  <div aria-hidden="true" style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: T.gold }} />
                  <div style={{ ...eyebrow, fontSize: '12px', color: T.goldDeep }}>FinAnalyse</div>
                </div>
              </div>
              {[
                { k: 'Prix d’un dossier', a: '5 000 – 15 000 €', b: 'dès 49 €', bnote: 'rapport complet dès 300 €' },
                { k: 'Délai', a: '2 à 4 semaines', b: '48 heures' },
                { k: 'Méthode', a: 'DCF · multiples · actif net', b: 'DCF · multiples · actif net', bnote: 'exactement les mêmes' },
                { k: 'Accès', a: 'sur réseau, gros dossiers', b: '100 % en ligne, toutes les PME' },
                { k: 'Engagement', a: 'honoraires engagés d’avance', b: 'satisfait ou remboursé sous 7 j' },
              ].map((row, i, arr) => (
                <div key={row.k} style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr 1fr', borderBottom: i < arr.length - 1 ? `1px solid ${T.ruleLight}` : 'none' }}>
                  <div style={{ padding: '18px 24px', fontFamily: FONT_BODY, fontSize: '14px', fontWeight: 600, color: T.ink }}>{row.k}</div>
                  <div style={{ padding: '18px 24px', borderLeft: `1px solid ${T.ruleLight}`, fontFamily: FONT_BODY, fontSize: '14.5px', color: T.muted }}>{row.a}</div>
                  <div style={{ padding: '18px 24px', borderLeft: `1px solid ${T.gold}`, background: T.paperWhite, fontFamily: FONT_BODY, fontSize: '14.5px', color: T.ink, fontWeight: 500 }}>
                    {row.b}{row.bnote && <span style={{ display: 'block', fontSize: '12.5px', color: T.goldDeep, fontWeight: 400, marginTop: '2px' }}>{row.bnote}</span>}
                  </div>
                </div>
              ))}
            </RevealOnScroll>
          </div>

          <RevealOnScroll as="p" style={{ marginTop: '20px', fontFamily: FONT_BODY, fontSize: '13.5px', color: T.muted, lineHeight: 1.6 }}>
            Tarifs indicatifs du marché pour une valorisation ou un dossier de cession en cabinet. Nos rapports ne remplacent pas un cabinet pour une transaction complexe de plusieurs millions d’euros — pour tout le reste, ils suffisent.
          </RevealOnScroll>
        </div>
      </section>

      {/* ══ 3 · HONORAIRES ══ */}
      <section id="honoraires" style={{ padding: '84px 32px', background: T.paper, borderBottom: `1px solid ${T.ink}` }}>
        <div style={{ maxWidth: '1240px', margin: '0 auto' }}>
          <SectionHead
            tag="§ 03 · Honoraires"
            title={<>Des honoraires <em style={{ fontStyle: 'italic', color: T.gold, fontWeight: 600 }}>connus d’avance</em>.</>}
            sub="Trois niveaux selon votre besoin. Prix fixes, sans abonnement, sans engagement — et l’Express à 49 € est déduit si vous commandez un rapport complet ensuite."
          />

          <div className="ed-3col" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0', borderTop: `1px solid ${T.ink}` }}>
            {OFFER_TIERS.map((tier, i) => (
              <RevealOnScroll as="article" key={tier.num} delay={i * 110} className="ed-tier" style={{ padding: '36px 28px 32px', borderRight: i < 2 ? `1px solid ${T.ruleLight}` : 'none', borderBottom: `1px solid ${T.ruleLight}`, display: 'flex', flexDirection: 'column', position: 'relative' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '18px' }}>
                  <span style={{ fontFamily: FONT_MONO, fontSize: '13px', color: T.goldDeep, letterSpacing: '0.05em' }}>{tier.num}</span>
                  <span style={{ fontFamily: FONT_MONO, fontSize: '15px', color: T.goldDeep, fontWeight: 500 }}>{tier.range}</span>
                </div>
                <h3 style={{ fontFamily: FONT_SERIF, fontSize: '26px', fontWeight: 500, color: T.ink, margin: '0 0 8px 0', letterSpacing: '-0.01em' }}>{tier.name}</h3>
                <p style={{ fontFamily: FONT_BODY, fontSize: '15.5px', fontWeight: 600, color: T.ink, margin: '0 0 10px 0', lineHeight: 1.45 }}>{tier.promise}</p>
                <p style={{ fontFamily: FONT_BODY, fontSize: '14.5px', lineHeight: 1.6, color: T.inkSoft, margin: '0 0 24px 0', flex: 1 }}>{tier.detail}</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  {tier.links.map((l) => l.kind === 'stripe' ? (
                    <a key={l.label} href={l.href} target="_blank" rel="noopener noreferrer"
                      onClick={() => trackCheckout(l.serviceName, l.price)}
                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '13px 4px', minHeight: '44px', borderTop: `1px solid ${T.ruleLight}`, textDecoration: 'none', fontFamily: FONT_BODY, fontSize: '14.5px', fontWeight: 600, color: T.ink }}
                      className="ed-tier-link">
                      {l.label} <ArrowRight size={15} className="ed-tier-arrow" style={{ color: T.goldDeep }} aria-hidden="true" />
                    </a>
                  ) : (
                    <Link key={l.label} to={l.to}
                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '13px 4px', minHeight: '44px', borderTop: `1px solid ${T.ruleLight}`, textDecoration: 'none', fontFamily: FONT_BODY, fontSize: '14.5px', fontWeight: 600, color: T.ink }}
                      className="ed-tier-link">
                      {l.label} <ArrowRight size={15} className="ed-tier-arrow" style={{ color: T.goldDeep }} aria-hidden="true" />
                    </Link>
                  ))}
                </div>
              </RevealOnScroll>
            ))}
          </div>

          <RevealOnScroll className="ed-pack" style={{ marginTop: '28px', padding: '22px 26px', background: T.paperWhite, border: `1px solid ${T.gold}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <div style={{ ...eyebrow, fontSize: '13px', color: T.goldDeep, marginBottom: '5px' }}>Pack complet — économie 1 100 €</div>
              <div style={{ fontFamily: FONT_BODY, fontSize: '15px', color: T.ink }}>
                Valorisation + Prévisionnel + Audit + Analyse + Business plan : <strong style={{ fontFamily: FONT_MONO }}>1 500 €</strong> au lieu de 2 600 €.
              </div>
            </div>
            <Link to="/tarifs" className="ed-btn-solid" style={{ padding: '14px 26px', background: T.ink, color: T.paper, textDecoration: 'none', fontSize: '13px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', border: `1px solid ${T.ink}`, whiteSpace: 'nowrap' }}>
              Tous les honoraires
            </Link>
          </RevealOnScroll>
          <p style={{ marginTop: '16px', fontFamily: FONT_BODY, fontSize: '13.5px', color: T.muted }}>
            Paiement en 2 ou 3 fois sans frais à partir de 500 €. Aucun paiement avant validation du devis pour les demandes sur mesure.
          </p>
        </div>
      </section>

      {/* ══ 4 · SIMULATEUR ══ */}
      <section id="simulateur" style={{ padding: '84px 32px', background: T.paperWarm, borderBottom: `1px solid ${T.ink}` }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <SectionHead
            tag="§ 04 · Estimation en ligne"
            title={<>Une première estimation, <em style={{ fontStyle: 'italic', color: T.gold, fontWeight: 600 }}>tout de suite</em>.</>}
            sub="Trois méthodes pondérées — multiples sectoriels, flux de trésorerie actualisés, actif net — les mêmes que dans nos rapports. Gratuit, sans inscription, résultat immédiat."
          />
          <RevealOnScroll><Simulator /></RevealOnScroll>
        </div>
      </section>

      {/* ══ 5 · MÉTHODE + GARANTIES ══ */}
      <section style={{ padding: '84px 32px', background: T.paper, borderBottom: `1px solid ${T.ink}` }}>
        <div style={{ maxWidth: '1240px', margin: '0 auto' }}>
          <SectionHead
            tag="§ 05 · La méthode"
            title={<>Trois étapes, <em style={{ fontStyle: 'italic', color: T.gold, fontWeight: 600 }}>48 heures</em>.</>}
          />

          <div className="ed-method-grid" style={{ display: 'grid', gridTemplateColumns: '1.25fr 0.75fr', gap: '56px', alignItems: 'start' }}>
            <div style={{ borderTop: `1px solid ${T.ink}` }}>
              {STEPS.map((s, i) => (
                <RevealOnScroll key={s.num} delay={i * 90} className="ed-step" style={{ display: 'grid', gridTemplateColumns: '70px 1fr', gap: '28px', alignItems: 'baseline', padding: '30px 4px', borderBottom: `1px solid ${T.ruleLight}` }}>
                  <span style={{ fontFamily: FONT_MONO, fontSize: '15px', color: T.goldDeep, letterSpacing: '0.05em' }}>{s.num}</span>
                  <div>
                    <h3 style={{ fontFamily: FONT_SERIF, fontSize: '23px', fontWeight: 500, color: T.ink, margin: '0 0 8px 0', letterSpacing: '-0.01em', lineHeight: 1.25 }}>{s.title}</h3>
                    <p style={{ fontFamily: FONT_BODY, fontSize: '15px', lineHeight: 1.6, color: T.inkSoft, margin: 0 }}>{s.desc}</p>
                  </div>
                </RevealOnScroll>
              ))}
            </div>

            <RevealOnScroll delay={140} className="ed-dcf-card" style={{ padding: '28px 24px', background: T.paperWarm, border: `1px solid ${T.ruleLight}`, alignSelf: 'stretch' }}>
              <div style={{ ...eyebrow, fontSize: '13px', color: T.goldDeep, marginBottom: '18px' }}>Sous le capot · Méthode DCF</div>
              <DCFSchema />
              <p style={{ fontFamily: FONT_BODY, fontSize: '13.5px', color: T.muted, lineHeight: 1.6, marginTop: '18px', marginBottom: 0 }}>
                Chaque flux futur est actualisé, la valeur terminale ajoutée, le tout pondéré avec les multiples sectoriels et l’actif net réévalué.
              </p>
            </RevealOnScroll>
          </div>

          <div className="ed-guarantees" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '32px', marginTop: '48px' }}>
            {GUARANTEES.map(({ icon: Icon, title, desc }, i) => (
              <RevealOnScroll key={title} delay={i * 80} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                <Icon size={20} style={{ color: T.goldDeep, flexShrink: 0, marginTop: '2px' }} aria-hidden="true" />
                <div>
                  <div style={{ fontFamily: FONT_BODY, fontSize: '15px', fontWeight: 600, color: T.ink, marginBottom: '3px' }}>{title}</div>
                  <div style={{ fontFamily: FONT_BODY, fontSize: '13.5px', color: T.muted, lineHeight: 1.5 }}>{desc}</div>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      <EditorialImageBand
        src="https://images.unsplash.com/photo-1481627834876-b7833e8f5570"
        alt="Bibliothèque — rayonnages de livres anciens"
        eyebrow="§ Cabinet · Note"
        quote="Lire un bilan, c'est lire une histoire — celle d'une entreprise, de ses choix, de ses cycles. Notre métier consiste à la rendre intelligible avant qu'elle ne devienne une valeur."
        caption="Méthode du cabinet, hiver 2026"
      />

      {/* ══ 6 · LE CABINET ══ */}
      <section style={{ padding: '84px 32px', background: T.paperWarm, borderBottom: `1px solid ${T.ink}` }}>
        <div style={{ maxWidth: '1240px', margin: '0 auto' }}>
          <SectionHead
            tag="§ 06 · Le cabinet"
            title={<>Qui analyse <em style={{ fontStyle: 'italic', color: T.gold, fontWeight: 600 }}>vos chiffres</em> ?</>}
          />

          <div className="ed-founder-grid" style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '56px', alignItems: 'start' }}>
            <RevealOnScroll>
              <div style={{ width: '180px', height: '180px', border: `1px solid ${T.ink}`, background: T.ink, color: T.paper, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONT_SERIF, fontSize: '56px', fontWeight: 500, marginBottom: '20px', position: 'relative', overflow: 'hidden' }}>
                <picture>
                  <source srcSet="/founder.webp" type="image/webp" />
                  <img src={FOUNDER.photo} alt={FOUNDER.name} width="180" height="180" loading="lazy" decoding="async" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                </picture>
                <span aria-hidden="true">{FOUNDER.initials}</span>
              </div>
              <div style={{ fontFamily: FONT_SERIF, fontSize: '22px', fontWeight: 500, color: T.ink }}>{FOUNDER.name}</div>
              <div style={{ fontFamily: FONT_BODY, fontSize: '14px', color: T.muted, marginTop: '4px', marginBottom: '24px' }}>{FOUNDER.role}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {FOUNDER.expertise.map(({ icon: Icon, label }) => (
                  <div key={label} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', fontFamily: FONT_BODY, fontSize: '13.5px', color: T.inkSoft, lineHeight: 1.5 }}>
                    <Icon size={16} style={{ color: T.goldDeep, flexShrink: 0, marginTop: '2px' }} aria-hidden="true" />
                    {label}
                  </div>
                ))}
              </div>
            </RevealOnScroll>

            <div>
              {FOUNDER.story.map((p, i) => (
                <RevealOnScroll as="p" key={i} delay={i * 70} style={{ fontFamily: FONT_BODY, fontSize: '16px', lineHeight: 1.7, color: T.inkSoft, margin: '0 0 18px 0' }}>{p}</RevealOnScroll>
              ))}
              <RevealOnScroll as="blockquote" style={{ margin: '28px 0 0 0', padding: '20px 26px', borderLeft: `2px solid ${T.gold}`, background: T.paperWhite }}>
                <p style={{ fontFamily: FONT_SERIF, fontSize: '18px', fontStyle: 'italic', lineHeight: 1.55, color: T.ink, margin: 0 }}>
                  {`« ${FOUNDER.quote} »`}
                </p>
              </RevealOnScroll>

              <RevealOnScroll as="figure" style={{ margin: '44px 0 0 0', padding: '28px 0 0 0', borderTop: `1px solid ${T.ink}` }}>
                <div style={{ ...eyebrow, fontSize: '13px', color: T.goldDeep, marginBottom: '14px' }}>
                  {`Témoignage vérifié · ${TESTIMONIAL.date}`}
                </div>
                <blockquote style={{ fontFamily: FONT_SERIF, fontSize: '21px', fontWeight: 400, fontStyle: 'italic', lineHeight: 1.5, color: T.ink, margin: '0 0 14px 0', maxWidth: '640px' }}>
                  {`« ${TESTIMONIAL.text} »`}
                </blockquote>
                <figcaption style={{ fontFamily: FONT_BODY, fontSize: '14px', color: T.muted }}>
                  <strong style={{ color: T.ink, fontWeight: 600 }}>{TESTIMONIAL.name}</strong>{` — ${TESTIMONIAL.role}`}
                </figcaption>
              </RevealOnScroll>

              <RevealOnScroll as="aside" style={{ marginTop: '36px', padding: '24px 28px', background: T.paperWhite, border: `1px solid ${T.ink}`, position: 'relative' }}>
                <div aria-hidden="true" style={{ position: 'absolute', top: '-1px', right: '-1px', width: '40px', height: '40px', background: T.gold, clipPath: 'polygon(100% 0, 100% 100%, 0 0)' }} />
                <div style={{ ...eyebrow, fontSize: '13px', color: T.goldDeep, marginBottom: '8px' }}>Offre fondateur · 10 places</div>
                <p style={{ fontFamily: FONT_BODY, fontSize: '15px', color: T.ink, lineHeight: 1.6, margin: '0 0 16px 0', maxWidth: '560px' }}>
                  Le cabinet est jeune, la méthode ne l’est pas. Pour constituer nos premiers témoignages publics :
                  <strong> −30 %</strong> contre un témoignage écrit signé, cumulable avec l’offre de lancement <strong>−20 %</strong> —
                  soit <strong style={{ fontFamily: FONT_MONO, color: T.goldDeep }}>50 % de remise</strong> sur votre première mission.
                </p>
                <a
                  href="mailto:contact@finanalyse.fr?subject=Offre%20fondateur%20-%20T%C3%A9moignage&body=Bonjour%2C%0A%0AJe%20suis%20int%C3%A9ress%C3%A9(e)%20par%20l'offre%20fondateur%20(t%C3%A9moignage%20vs%20-50%25)."
                  className="ed-btn-solid"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', padding: '14px 26px', background: T.ink, color: T.paper, border: `1px solid ${T.ink}`, fontFamily: FONT_BODY, fontSize: '13px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', textDecoration: 'none' }}
                >
                  Réserver ma place <ArrowRight size={15} aria-hidden="true" />
                </a>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* ══ 7 · FAQ ══ */}
      <section style={{ padding: '84px 32px', background: T.paper, borderBottom: `1px solid ${T.ink}` }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <SectionHead
            tag="§ 07 · Questions"
            title={<>Ce qu’on nous demande <em style={{ fontStyle: 'italic', color: T.gold, fontWeight: 600 }}>avant de commander</em>.</>}
          />
          <RevealOnScroll style={{ borderTop: `1px solid ${T.ink}` }}>
            {FAQS.map((f, i) => (
              <FaqItem key={i} idx={i} q={f.q} a={f.a} open={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? null : i)} />
            ))}
          </RevealOnScroll>
          <p style={{ marginTop: '24px', fontFamily: FONT_BODY, fontSize: '14.5px', color: T.muted }}>
            D’autres questions ?{' '}
            <Link to="/faq" className="ed-link-underline" style={{ color: T.ink, fontWeight: 500, textDecoration: 'none' }}>
              Consultez la FAQ complète
            </Link>
            {' '}ou écrivez-nous : contact@finanalyse.fr — réponse sous 24 h ouvrées.
          </p>
        </div>
      </section>

      {/* ══ 7b · RESSOURCES — le blog en vitrine ═════════════════════════════ */}
      <section style={{ padding: '80px 32px', background: T.paperWarm, borderBottom: `1px solid ${T.ink}` }}>
        <div style={{ maxWidth: '1240px', margin: '0 auto' }}>
          <SectionHead
            tag="§ 08 · Ressources"
            title={<>Nos guides pour <em style={{ fontStyle: 'italic', color: T.gold, fontWeight: 600 }}>dirigeants pressés</em>.</>}
            sub="Des articles courts, chiffrés, sans jargon inutile — écrits par le cabinet, pour vous aider à décider avant même de nous solliciter."
          />

          <div className="ed-3col" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0', borderTop: `1px solid ${T.ink}` }}>
            {[
              { num: '01', tag: 'Financement bancaire', title: 'Prévisionnel financier 3 ans : la méthode complète pour PME', read: '10 min', slug: 'previsionnel-financier-3-ans-methode-modele-pme-2026' },
              { num: '02', tag: 'Valorisation', title: 'Combien vaut une entreprise selon son secteur ? Multiples 2026', read: '9 min', slug: 'combien-vaut-une-entreprise-par-secteur-multiples-2026' },
              { num: '03', tag: 'Ratios bancaires', title: 'Calculer son DSCR : le ratio que votre banquier regarde en premier', read: '7 min', slug: 'calculer-dscr-pme-capacite-remboursement-bancaire-2026' },
            ].map((a, i) => (
              <RevealOnScroll as="article" key={a.slug} delay={i * 110} style={{ padding: '32px 28px 28px', borderRight: i < 2 ? `1px solid ${T.ruleLight}` : 'none', borderBottom: `1px solid ${T.ruleLight}`, display: 'flex', flexDirection: 'column' }}>
                <div style={{ fontFamily: FONT_MONO, fontSize: '13px', color: T.goldDeep, letterSpacing: '0.05em', marginBottom: '14px' }}>{a.num}</div>
                <div style={{ ...eyebrow, fontSize: '11px', color: T.muted, marginBottom: '10px' }}>{`${a.tag} · ${a.read}`}</div>
                <h3 style={{ fontFamily: FONT_SERIF, fontSize: '20px', fontWeight: 500, color: T.ink, margin: '0 0 20px 0', letterSpacing: '-0.01em', lineHeight: 1.3, flex: 1 }}>{a.title}</h3>
                <Link to={`/blog/${a.slug}`} className="ed-link-underline" style={{ fontFamily: FONT_BODY, fontSize: '14px', fontWeight: 600, color: T.ink, textDecoration: 'none', minHeight: '44px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                  Lire l’article <ArrowRight size={14} aria-hidden="true" />
                </Link>
              </RevealOnScroll>
            ))}
          </div>

          <RevealOnScroll as="p" style={{ marginTop: '24px', fontFamily: FONT_BODY, fontSize: '14.5px', color: T.muted }}>
            13 guides publiés à ce jour ·{' '}
            <Link to="/blog" className="ed-link-underline" style={{ color: T.ink, fontWeight: 500, textDecoration: 'none' }}>
              Voir tous les articles →
            </Link>
          </RevealOnScroll>
        </div>
      </section>

      {/* ══ 8 · CTA FINAL + FORMULAIRE ══ */}
      <section id="contact" style={{ padding: '100px 32px 92px', background: T.ink, color: T.paper, position: 'relative', overflow: 'hidden' }}>
        <Guilloche size={340} color={T.gold} opacity={0.14} rings={15} style={{ position: 'absolute', top: '-110px', left: '-110px', zIndex: 0, animation: spin('edSpin 80s linear infinite') }} />
        {/* Filigrane décoratif : le mot est rendu par CSS (.ed-watermark::after,
            plus bas) et non écrit ici. Son opacité de 0,07 est voulue — c'est une
            texture, pas du contenu — mais un vrai nœud texte à 1,07:1 était relevé
            comme défaut de contraste par les audits (audit 2026-08-15). En contenu
            généré, il reste identique à l'écran sans être traité comme du texte. */}
        <div aria-hidden="true" className="ed-watermark" style={{ position: 'absolute', right: '-60px', bottom: '-40px', fontFamily: FONT_SERIF, fontSize: 'clamp(140px, 18vw, 220px)', fontWeight: 400, fontStyle: 'italic', color: T.gold, opacity: 0.07, lineHeight: 1, pointerEvents: 'none', userSelect: 'none', letterSpacing: '-0.04em' }} />

        <div style={{ position: 'relative', zIndex: 2, maxWidth: '900px', margin: '0 auto' }}>
          <RevealOnScroll style={{ textAlign: 'center', marginBottom: '56px' }}>
            <div style={{ ...eyebrow, fontSize: '13px', color: T.goldSoft, marginBottom: '20px' }}>Par où commencer ?</div>
            <h2 style={{ fontFamily: FONT_SERIF, fontSize: 'clamp(34px, 5vw, 60px)', fontWeight: 400, color: T.paper, margin: '0 0 18px 0', letterSpacing: '-0.015em', lineHeight: 1.06 }}>
              L’estimation gratuite prend <em style={{ fontStyle: 'italic', color: T.gold }}>deux minutes</em>.
            </h2>
            <p style={{ fontFamily: FONT_BODY, fontSize: '16.5px', color: 'rgba(250,248,243,0.75)', maxWidth: '560px', margin: '0 auto 32px', lineHeight: 1.6 }}>
              Sans inscription, sans engagement. Et si vous savez déjà ce qu’il vous faut, décrivez votre situation ci-dessous — devis sous 24 h ouvrées.
            </p>
            <MagneticButton href="#simulateur" strength={0.3} maxOffset={12} className="ed-cta-gold" style={{ alignItems: 'center', gap: '12px', padding: '18px 36px', background: T.gold, color: T.ink, border: `1px solid ${T.gold}`, fontFamily: FONT_BODY, fontSize: '14px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', textDecoration: 'none' }}>
              Estimer ma PME — gratuit <ArrowRight size={16} aria-hidden="true" />
            </MagneticButton>
          </RevealOnScroll>

          <RevealOnScroll style={{ background: T.paper, color: T.ink, border: `1px solid ${T.gold}`, padding: '40px 36px' }} className="ed-form-card">
            {formSent ? (
              <div>
                <div style={{ textAlign: 'center', marginBottom: '28px' }}>
                  <Check size={44} style={{ color: T.goldDeep, margin: '0 auto 12px' }} aria-hidden="true" />
                  <h3 style={{ fontFamily: FONT_SERIF, fontSize: '26px', fontWeight: 500, margin: '0 0 6px 0' }}>
                    Demande reçue, merci {formData.name?.split(' ')[0] || ''}.
                  </h3>
                  <p style={{ fontFamily: FONT_BODY, color: T.inkSoft, fontSize: '14.5px', margin: 0 }}>
                    {files.length > 0
                      ? `${files.length} document${files.length > 1 ? 's' : ''} bien réceptionné${files.length > 1 ? 's' : ''}.`
                      : 'Nous avons bien reçu votre demande.'}
                  </p>
                </div>
                <div style={{ borderTop: `1px solid ${T.ruleLight}`, paddingTop: '22px' }}>
                  <div style={{ ...eyebrow, fontSize: '13px', color: T.goldDeep, marginBottom: '14px' }}>Et maintenant ?</div>
                  <ol style={{ paddingLeft: '20px', margin: 0 }}>
                    <li style={{ fontSize: '14.5px', color: T.inkSoft, marginBottom: '10px', lineHeight: 1.6 }}>
                      <strong style={{ color: T.ink }}>Email de confirmation envoyé</strong> à {formData.email || 'votre adresse'} — vérifiez aussi vos spams.
                    </li>
                    <li style={{ fontSize: '14.5px', color: T.inkSoft, marginBottom: '10px', lineHeight: 1.6 }}>
                      <strong style={{ color: T.ink }}>Sous 24 h ouvrées</strong> — réponse personnalisée avec devis détaillé.
                    </li>
                    <li style={{ fontSize: '14.5px', color: T.inkSoft, marginBottom: '10px', lineHeight: 1.6 }}>
                      <strong style={{ color: T.ink }}>Validation puis paiement</strong> — aucun engagement avant votre accord.
                    </li>
                    <li style={{ fontSize: '14.5px', color: T.inkSoft, lineHeight: 1.6 }}>
                      <strong style={{ color: T.ink }}>Livraison sous 48 h</strong> — rapport PDF + échange de 30 minutes inclus.
                    </li>
                  </ol>
                  <div style={{ marginTop: '22px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <a href="mailto:contact@finanalyse.fr" style={{ padding: '12px 22px', background: 'transparent', color: T.ink, border: `1px solid ${T.ink}`, textDecoration: 'none', fontSize: '13px', fontWeight: 600, minHeight: '44px', display: 'inline-flex', alignItems: 'center' }}>
                      Écrire à contact@finanalyse.fr
                    </a>
                    <button onClick={() => { setFormSent(false); setFormData({ name: '', email: '', company: '', phone: '', service: '', message: '' }); setFiles([]); }} style={{ padding: '12px 22px', background: 'transparent', color: T.muted, border: `1px solid ${T.ruleLight}`, fontSize: '13px', cursor: 'pointer', fontFamily: FONT_BODY, minHeight: '44px' }}>
                      Nouvelle demande
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div style={{ ...eyebrow, fontSize: '13px', color: T.goldDeep, marginBottom: '22px' }}>Démarrer une mission · Réponse sous 24 h</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '16px' }}>
                  {[
                    { key: 'name', label: 'Nom complet *', type: 'text', placeholder: 'Jean Martin', required: true },
                    { key: 'email', label: 'Email professionnel *', type: 'email', placeholder: 'jean@entreprise.fr', required: true },
                    { key: 'company', label: 'Entreprise', type: 'text', placeholder: 'Mon Entreprise SAS', required: false },
                    { key: 'phone', label: 'Téléphone', type: 'tel', placeholder: '06 12 34 56 78', required: false },
                  ].map(({ key, label, type, placeholder, required }) => (
                    <div key={key}>
                      <label htmlFor={`mission-${key}`} style={labelStyle}>{label}</label>
                      <input id={`mission-${key}`} required={required} type={type} value={formData[key]} onChange={e => setFormData({ ...formData, [key]: e.target.value })} placeholder={placeholder} style={inputStyle} />
                    </div>
                  ))}
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label htmlFor="mission-service" style={labelStyle}>Service souhaité</label>
                  <select id="mission-service" value={formData.service} onChange={e => setFormData({ ...formData, service: e.target.value })} style={{ ...inputStyle, cursor: 'pointer' }}>
                    <option value="">Sélectionner un service…</option>
                    <option>Diagnostic gratuit — 0€</option>
                    <option>Pré-diagnostic Express — 49€</option>
                    <option>Scoring bancaire — 200€</option>
                    <option>Analyse financière — 300€</option>
                    <option>Audit de trésorerie — 350€</option>
                    <option>Prévisionnel financier — 500€</option>
                    <option>Business Plan complet — 700€</option>
                    <option>Valorisation d'entreprise — 900€</option>
                    <option>Dossier reprise/cession — 1 200€</option>
                    <option>Tableau de bord mensuel — 150€/mois</option>
                    <option>Pack complet — 1 500€</option>
                  </select>
                </div>

                <div style={{ marginBottom: '18px' }}>
                  <label htmlFor="mission-message" style={labelStyle}>Décrivez votre situation</label>
                  <textarea id="mission-message" value={formData.message} onChange={e => setFormData({ ...formData, message: e.target.value })} rows={4} placeholder="Ex : je cherche une valorisation pour céder mon entreprise dans 6 mois. CA 800 k€, secteur BTP, Île-de-France…" style={{ ...inputStyle, resize: 'vertical' }} />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label htmlFor="file-upload" style={{ display: 'block', padding: '20px', border: `1px dashed ${files.length > 0 ? T.goldDeep : T.ruleLight}`, textAlign: 'center', cursor: 'pointer', background: T.paperWhite }}>
                    <input id="file-upload" type="file" multiple
                      accept=".pdf,.jpg,.jpeg,.png,.xls,.xlsx,.doc,.docx,.csv"
                      onChange={e => { setFormError(null); setFiles(Array.from(e.target.files)); }}
                      style={{ display: 'none' }} />
                    {files.length === 0 ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', fontSize: '14px', fontWeight: 500, color: T.ink }}>
                        <Paperclip size={16} style={{ color: T.goldDeep }} aria-hidden="true" />
                        Joindre vos documents — facultatif à cette étape
                      </span>
                    ) : (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', fontSize: '14px', fontWeight: 600, color: T.ink }}>
                        <Check size={16} style={{ color: T.goldDeep }} aria-hidden="true" />
                        {files.length} fichier{files.length > 1 ? 's' : ''} — {(files.reduce((s, f) => s + f.size, 0) / 1024 / 1024).toFixed(1)} Mo
                      </span>
                    )}
                    <span style={{ display: 'block', fontSize: '13px', color: T.muted, marginTop: '6px' }}>
                      {files.length === 0
                        ? 'Bilans, liasse fiscale, comptes de résultat — PDF, Excel, Word, images. 5 fichiers max, 3 Mo chacun.'
                        : files.map(f => f.name).join(' · ')}
                    </span>
                  </label>
                  <p style={{ fontSize: '13px', color: T.muted, marginTop: '8px', lineHeight: 1.55 }}>
                    Pas de documents sous la main ? Envoyez le formulaire seul : nous vous indiquerons quoi transmettre, avec un lien sécurisé de dépôt.
                  </p>
                </div>

                <button type="submit" disabled={formSubmitting} className="ed-submit" style={{ width: '100%', padding: '17px', fontSize: '14px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', background: formSubmitting ? T.muted : T.ink, color: T.paper, border: `1px solid ${formSubmitting ? T.muted : T.ink}`, cursor: formSubmitting ? 'not-allowed' : 'pointer', fontFamily: FONT_BODY, minHeight: '48px' }}>
                  {formSubmitting
                    ? (files.length > 0 ? `Envoi des ${files.length} fichier(s)…` : 'Envoi en cours…')
                    : 'Envoyer ma demande — réponse sous 24 h'}
                </button>
                {formError && (
                  <div role="alert" style={{ marginTop: '14px', padding: '12px 16px', border: `1px solid ${T.danger}`, fontSize: '13.5px', color: T.danger, textAlign: 'center', background: T.paperWhite }}>
                    {formError}
                  </div>
                )}
                <p style={{ fontSize: '13px', color: T.muted, textAlign: 'center', marginTop: '14px', marginBottom: 0, fontFamily: FONT_BODY }}>
                  Données chiffrées · Hébergement France · Conforme RGPD · Paiement sécurisé Stripe — aucun débit avant validation du devis.
                </p>
              </form>
            )}
          </RevealOnScroll>
        </div>
      </section>

      </main>

      <Footer />

      </div>

      <style>{`
        @keyframes edSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes edGlow { 0%,100% { opacity: 0.55; transform: scale(1); } 50% { opacity: 1; transform: scale(1.08); } }
        @keyframes edFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
        @keyframes edShimmer { 0% { background-position: -140% 0; } 100% { background-position: 240% 0; } }
        @keyframes edCue { 0%,100% { transform: translateY(0); opacity: 0.5; } 50% { transform: translateY(5px); opacity: 1; } }

        .ed-glow { animation: edGlow 7s ease-in-out infinite; }
        .ed-float { animation: edFloat 6s ease-in-out infinite; }
        .ed-scroll-cue svg { animation: edCue 1.8s ease-in-out infinite; }

        .ed-shimmer {
          background: linear-gradient(100deg, ${T.gold} 30%, ${T.goldSoft} 50%, ${T.gold} 70%);
          background-size: 200% auto;
          -webkit-background-clip: text; background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: edShimmer 6s linear infinite;
        }

        .ed-cta-primary::before {
          content: ''; position: absolute; inset: 0; background: ${T.gold};
          transform: translateY(100%); transition: transform 0.45s cubic-bezier(0.16,1,0.3,1); z-index: 0;
        }
        .ed-cta-primary:hover::before { transform: translateY(0); }
        .ed-cta-primary:hover { color: ${T.ink}; border-color: ${T.gold}; }
        .ed-cta-gold { transition: transform 0.4s cubic-bezier(0.16,1,0.3,1), box-shadow 0.4s ease; }
        .ed-cta-gold:hover { box-shadow: 0 16px 40px -12px ${T.gold}; }

        .ed-link-underline { position: relative; }
        .ed-link-underline::after {
          content: ''; position: absolute; left: 0; bottom: 0; height: 1px; width: 100%;
          background: ${T.gold}; transform: scaleX(0); transform-origin: right;
          transition: transform 0.4s cubic-bezier(0.16,1,0.3,1);
        }
        .ed-link-underline:hover::after { transform: scaleX(1); transform-origin: left; }

        .ed-btn-solid { transition: transform 0.3s ease, box-shadow 0.3s ease; }
        .ed-btn-solid:hover { transform: translateY(-2px); box-shadow: 0 12px 28px -14px rgba(10,25,41,0.6); }
        .ed-submit { transition: transform 0.25s ease, box-shadow 0.3s ease; }
        .ed-submit:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 14px 30px -14px rgba(10,25,41,0.5); }

        .ed-tier { transition: background 0.4s ease; }
        .ed-tier:hover { background: ${T.paperWhite}; }
        .ed-tier-link { transition: color 0.25s ease, padding-left 0.3s ease; }
        .ed-tier-link:hover { color: ${T.goldDeep}; padding-left: 10px; }
        .ed-tier-arrow { transition: transform 0.3s cubic-bezier(0.16,1,0.3,1); }
        .ed-tier-link:hover .ed-tier-arrow { transform: translateX(5px); }
        .ed-report-sample { transition: background 0.4s ease; }
        .ed-report-sample:hover { background: ${T.paperWhite}; }
        .ed-faq-btn:hover { color: ${T.goldDeep}; }

        /* Filigrane décoratif rendu en contenu généré : identique à l'écran,
           mais ce n'est plus un nœud texte que les audits comptabilisent. */
        .ed-watermark::after { content: 'finanalyse.'; }

        .ed-form-card input:focus, .ed-form-card select:focus, .ed-form-card textarea:focus {
          border-color: ${T.ink}; box-shadow: 0 0 0 1px ${T.ink};
        }

        @media (prefers-reduced-motion: reduce) {
          .ed-glow, .ed-float, .ed-scroll-cue svg, .ed-shimmer { animation: none !important; }
          .ed-shimmer { -webkit-text-fill-color: ${T.gold}; }
        }

        @media (max-width: 1024px) {
          .ed-hero-grid { grid-template-columns: 1fr !important; gap: 48px !important; }
          .ed-founder-grid { grid-template-columns: 1fr !important; gap: 36px !important; }
          .ed-method-grid { grid-template-columns: 1fr !important; gap: 36px !important; }
        }
        @media (max-width: 860px) {
          .ed-3col { grid-template-columns: 1fr !important; }
          .ed-3col article { border-right: none !important; }
          .ed-guarantees { grid-template-columns: repeat(2, 1fr) !important; }
          .ed-section-head { grid-template-columns: 1fr !important; gap: 12px !important; }
          .ed-trust-grid { grid-template-columns: 1fr !important; gap: 22px !important; }
          .ed-trust-badges { justify-content: flex-start !important; }
          .ed-rail { display: none; }
        }
        @media (max-width: 560px) {
          .ed-guarantees { grid-template-columns: 1fr !important; }
          .ed-pack { flex-direction: column !important; align-items: flex-start !important; }
          .ed-form-card { padding: 28px 20px !important; }
        }

        /* ── HERO MOBILE : compacter pour que le CTA « Estimer ma PME » soit visible
              dès le 1er écran (le vieux CSS .hero-h1-home-mag ne ciblait plus le markup refondu) ── */
        @media (max-width: 768px) {
          .ed-hero-header { padding-top: 40px !important; }
          .ed-hero-header > div { padding-bottom: 32px !important; }
          .ed-hero-h1 {
            font-size: clamp(29px, 8.4vw, 40px) !important;
            line-height: 1.08 !important;
            margin-bottom: 22px !important;
          }
          .ed-hero-grid > div > p { font-size: 16px !important; margin-bottom: 24px !important; }
          .ed-cta-primary { width: 100%; justify-content: center; }
        }
      `}</style>
    </div>
  );
}
